import React, { useCallback, useMemo, useState } from 'react';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import moment from 'moment';
import Select from 'react-select';
import { toast } from 'sonner';
import { AlertTriangle, Pencil } from 'lucide-react';
import { isAdmin, isClient } from '../../../helpers/global';
import useAppChoices from '../../../hooks/useAppChoices';
import CircularProgressBar from '../../circular-progress';
import QBIcon from '../../icons/QBIcon';
import UnderlineButton from '../../UnderlineButton';
import AssignedProcessorsMultiSelectModal from './Form/Common/AssignedProcessorsMultiSelect';
import InlineEditSaveCancel from './InlineEditSaveCancel';

const INLINE_NUMBER_KEYS = new Set(['hardCost', 'processingFee']);
const INLINE_TEXT_KEYS = new Set([
  'listingAgentName',
  'listingAgentPhone',
  'titleCertification',
  'buyerCertification',
  'lenderCertification',
  'underwriterCertification'
]);
const INLINE_EDIT_KEYS = new Set([
  'assignedProcessors',
  'assignedReportAnalysts',
  'assignedSurveyor',
  'rush',
  ...INLINE_NUMBER_KEYS,
  ...INLINE_TEXT_KEYS
]);

const valueRowMinStyle = { minHeight: 28 };

const OrderDetailField = ({
  field,
  order,
  dateFields,
  priceFields,
  hardCostRequiredStatuses,
  formatBooleans = false,
  role,
  currentUserInfo,
  loadingMetaData,
  onSyncOrderHardcost,
  disabled = false,
  onOrderFieldUpdate,
  allowInlineEditing = true
}) => {
  const { label, key, icon, objectKey, isSpecial, render, labelWarningTooltipText } = field;
  const raw = order[key];
  const isDate = dateFields.includes(key);
  const isPrice = priceFields.includes(key);
  const isNullOrUndefinedOrEmpty =
    raw === null || raw === undefined || raw === '' || raw?.length === 0;

  const isMissingRequiredHardCost =
    key === 'hardCost' &&
    isNullOrUndefinedOrEmpty &&
    hardCostRequiredStatuses.includes(order['status']);

  const surveyors = useAppChoices('Surveyor');

  const [hovering, setHovering] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftNumber, setDraftNumber] = useState('');
  const [draftText, setDraftText] = useState('');
  const [draftSelect, setDraftSelect] = useState('');
  const [draftRush, setDraftRush] = useState(false);
  const [showProcessorsModal, setShowProcessorsModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const canTryInline =
    Boolean(onOrderFieldUpdate) &&
    allowInlineEditing &&
    (!disabled || editing || saving) &&
    !isClient(role);
  const isInlineEditable = canTryInline && INLINE_EDIT_KEYS.has(key) && !isSpecial;

  const runUpdate = useCallback(
    async payload => {
      if (!onOrderFieldUpdate) return;
      setSaving(true);
      try {
        const response = await onOrderFieldUpdate(payload);
        if (response !== false) {
          setEditing(false);
          setShowProcessorsModal(false);
        }
      } finally {
        setSaving(false);
      }
    },
    [onOrderFieldUpdate]
  );

  let value;
  if (isSpecial && render) {
    value = render();
  } else if (isNullOrUndefinedOrEmpty) {
    value = 'N/A';
  } else if (isDate) {
    value = moment(raw).format('MMM Do, YYYY');
  } else if (isPrice) {
    value = `$${Number(raw).toFixed(2)}`;
  } else if (key === 'rush' && typeof raw === 'boolean') {
    value = raw ? 'Yes' : 'No';
  } else if (formatBooleans && typeof raw === 'boolean') {
    value = raw ? 'Yes' : 'No';
  } else if (Array.isArray(raw)) {
    if (objectKey === 'email') {
      const items = raw.map(a => a?.[objectKey]).filter(Boolean);
      value =
        items.length > 0
          ? items.map((email, i) => (
            <React.Fragment key={`${email}-${i}`}>
              <a href={`mailto:${email}`} className="text-info">
                {email}
              </a>
              {i < items.length - 1 ? ', ' : ''}
            </React.Fragment>
          ))
          : 'N/A';
    } else {
      value = raw.map(a => a?.[objectKey]).join(', ');
    }
  } else {
    value = raw;
  }

  const beginEdit = useCallback(() => {
    if (key === 'assignedProcessors' || key === 'assignedReportAnalysts') {
      setShowProcessorsModal(true);
      return;
    }
    if (INLINE_NUMBER_KEYS.has(key)) {
      const n = order[key];
      setDraftNumber(n === null || n === undefined || n === '' ? '' : String(n));
    }
    if (INLINE_TEXT_KEYS.has(key)) {
      const v = order[key];
      if (v == null) setDraftText('');
      else if (typeof v === 'boolean') setDraftText(v ? 'Yes' : 'No');
      else setDraftText(String(v));
    }
    if (key === 'assignedSurveyor') {
      setDraftSelect(order[key] || '');
    }
    if (key === 'rush') {
      setDraftRush(Boolean(order.rush));
    }
    setEditing(true);
  }, [key, order]);

  const cancelEdit = useCallback(() => {
    setShowProcessorsModal(false);
    setEditing(false);
  }, []);

  const saveNumberField = useCallback(async () => {
    const trimmed = String(draftNumber).trim();
    const num = trimmed === '' ? null : Number(trimmed);
    if (trimmed !== '' && Number.isNaN(num)) {
      toast.error('Enter a valid number.');
      return;
    }
    if (
      key === 'hardCost' &&
      hardCostRequiredStatuses.includes(order['status']) &&
      (num === null || num === undefined || trimmed === '')
    ) {
      toast.error('Hard cost is required for this status.');
      return;
    }
    await runUpdate({ [key]: num });
  }, [draftNumber, hardCostRequiredStatuses, key, order, runUpdate]);

  const saveRush = useCallback(async () => {
    await runUpdate({ rush: draftRush });
  }, [draftRush, runUpdate]);

  const saveTextField = useCallback(async () => {
    const trimmed = draftText.trim();
    await runUpdate({ [key]: trimmed === '' ? null : trimmed });
  }, [draftText, key, runUpdate]);

  const saveSelectField = useCallback(async () => {
    const trimmed = String(draftSelect || '').trim();
    await runUpdate({ [key]: trimmed === '' ? null : trimmed });
  }, [draftSelect, key, runUpdate]);

  const onProcessorsModalConfirm = useCallback(async selected => {
    if (key === 'assignedProcessors' && !selected?.length) {
      toast.error('Select at least one assigned processor.');
      return;
    }

    const currentIds = new Set((order[key] || []).map(p => p?._id || p));
    const selectedIds = new Set(selected.map(a => a?._id || a));

    if (
      currentIds.size === selectedIds.size &&
      [...currentIds].every(id => selectedIds.has(id))
    ) {
      setShowProcessorsModal(false);
      return;
    }

    await runUpdate({ [key]: selected.map(a => a?._id || a) });
  }, [key, order, runUpdate]);

  const editIconColumn = isInlineEditable && !editing && (
    <div
      className="d-flex align-items-center justify-content-end flex-shrink-0"
      style={{ width: 24, minHeight: valueRowMinStyle.minHeight }}
    >
      <button
        type="button"
        className="btn btn-link p-0 d-inline-flex align-items-center justify-content-center text-muted"
        style={{
          opacity: hovering ? 1 : 0,
          transition: 'opacity 0.12s ease',
          pointerEvents: hovering ? 'auto' : 'none',
          width: 24,
          height: 24
        }}
        aria-label={`Edit ${label}`}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          beginEdit();
        }}
      >
        <Pencil size={14} className="flex-shrink-0"/>
      </button>
    </div>
  );

  const renderReadonlyValueBlock = () => (
    <>
      <p className={`font-weight-medium largeFont mb-0 ${isMissingRequiredHardCost ? 'text-danger' : ''}`}>
        {value || 'N/A'}
      </p>
      {key === 'hardCost' && isAdmin(role) && currentUserInfo?.hasFinancialIntelligenceAccess && !editing && (
        <UnderlineButton
          className="ml-2 tinyFont"
          Icon={() =>
            loadingMetaData?.quickBooksExpenses ? (
              <CircularProgressBar size={1.3} className="mr-2 text-muted" />
            ) : (
              <QBIcon className="mr-2" width={16} height={16} />
            )
          }
          variant="dark"
          iconSize={20}
          text={loadingMetaData?.quickBooksExpenses ? 'Getting from QB...' : 'Get From QB'}
          onClick={() => onSyncOrderHardcost?.(order?._id)}
          disabled={loadingMetaData?.quickBooksExpenses}
        />
      )}
    </>
  );

  const renderValueRow = () => {
    if (isSpecial && render) {
      return value;
    }

    if (!isInlineEditable) {
      return (
        <div className="d-flex align-items-center flex-wrap" style={valueRowMinStyle}>
          {renderReadonlyValueBlock()}
        </div>
      );
    }

    if (editing && INLINE_TEXT_KEYS.has(key)) {
      return (
        <div className="d-flex align-items-center w-100 flex-wrap" style={valueRowMinStyle}>
          <Form.Control
            size="sm"
            type={key === 'listingAgentPhone' ? 'tel' : 'text'}
            className="mr-1"
            style={{ maxWidth: 240, flex: '1 1 140px' }}
            value={draftText}
            onChange={e => setDraftText(e.target.value)}
            disabled={saving}
            onKeyDown={e => {
              if (e.key === 'Enter') void saveTextField();
              if (e.key === 'Escape') cancelEdit();
            }}
            aria-label={label}
          />
          <InlineEditSaveCancel
            saving={saving}
            onSave={saveTextField}
            onCancel={cancelEdit}
            saveAriaLabel={`Save ${label}`}
          />
        </div>
      );
    }

    if (editing && key === 'assignedSurveyor') {
      const selectOptions = [
        ...(draftSelect && !surveyors.includes(draftSelect) ? [draftSelect] : []),
        ...surveyors
      ].map(surveyor => ({ label: surveyor, value: surveyor }));
      const selectedOption = draftSelect ? { label: draftSelect, value: draftSelect } : null;
      return (
        <div className="d-flex align-items-center w-100 flex-wrap" style={valueRowMinStyle}>
          <div className="mr-2" style={{ maxWidth: 240, flex: '1 1 140px', minWidth: 140 }} onClick={e => e.stopPropagation()}>
            <Select
              inputId="assigned-surveyor-select"
              name="assignedSurveyor"
              placeholder="Select surveyor"
              options={selectOptions}
              value={selectedOption}
              isClearable
              isDisabled={saving}
              className="basic-single select-organization"
              classNamePrefix="select"
              menuPlacement="auto"
              aria-label={label}
              onChange={option => setDraftSelect(option?.value || '')}
              styles={{
                control: base => ({
                  ...base,
                  minHeight: 31,
                  height: 31
                }),
                valueContainer: base => ({
                  ...base,
                  padding: '0 8px'
                }),
                indicatorsContainer: base => ({
                  ...base,
                  height: 31
                }),
                input: base => ({
                  ...base,
                  margin: 0,
                  padding: 0
                })
              }}
            />
          </div>
          <InlineEditSaveCancel
            saving={saving}
            onSave={saveSelectField}
            onCancel={cancelEdit}
            saveAriaLabel={`Save ${label}`}
          />
        </div>
      );
    }

    if (editing && INLINE_NUMBER_KEYS.has(key)) {
      return (
        <div className="d-flex align-items-center w-100 flex-wrap" style={valueRowMinStyle}>
          <Form.Control
            size="sm"
            type="number"
            className="mr-1"
            style={{ maxWidth: 120 }}
            value={draftNumber}
            onChange={e => setDraftNumber(e.target.value)}
            disabled={saving}
            onKeyDown={e => {
              if (e.key === 'Enter') void saveNumberField();
              if (e.key === 'Escape') cancelEdit();
            }}
          />
          <InlineEditSaveCancel saving={saving} onSave={saveNumberField} onCancel={cancelEdit} />
        </div>
      );
    }

    if (editing && key === 'rush') {
      return (
        <div className="d-flex align-items-center w-100 flex-wrap" style={valueRowMinStyle}>
          <Form.Control
            as="select"
            size="sm"
            className="mr-2"
            style={{ maxWidth: 100 }}
            value={draftRush ? 'yes' : 'no'}
            onChange={e => setDraftRush(e.target.value === 'yes')}
            disabled={saving}
            aria-label="Rush"
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </Form.Control>
          <InlineEditSaveCancel saving={saving} onSave={saveRush} onCancel={cancelEdit} />
        </div>
      );
    }

    return (
      <div className="d-flex align-items-center w-100" style={valueRowMinStyle}>
        <div className="d-flex align-items-center flex-wrap">{renderReadonlyValueBlock()}</div>
        {editIconColumn}
      </div>
    );
  };

  const tooltipId = useMemo(() => `label-warning-tooltip-${String(label).replace(/\s+/g, '-')}`, [label]);

  return (
    <div className="col-12 col-sm-6 col-md-6 col-lg-3 mb-2">
      <div
        className="d-flex align-items-start order-detail-field-wrap"
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        <div className="d-flex flex-column align-items-start flex-grow-1">
          <div className="d-flex align-items-center mb-1 w-100">
            <div className="mr-2 d-flex align-items-center flex-shrink-0 text-muted">{icon}</div>

            <p className="text-muted mb-0 midFont d-flex align-items-center">
              {label}
              {labelWarningTooltipText && (
                <OverlayTrigger
                  placement="bottom"
                  delay={50}
                  overlay={<Tooltip id={tooltipId}>{labelWarningTooltipText}</Tooltip>}
                >
                  <AlertTriangle size={13} className="ml-1" style={{ color: '#f59e0b' }} />
                </OverlayTrigger>
              )}
            </p>
          </div>
          <div className="d-flex align-items-center w-100">{renderValueRow()}</div>
        </div>
      </div>

      {(key === 'assignedProcessors' || key === 'assignedReportAnalysts') && (
        <AssignedProcessorsMultiSelectModal
          show={showProcessorsModal}
          onHide={() => setShowProcessorsModal(false)}
          onConfirm={onProcessorsModalConfirm}
          prevData={order[key] || []}
          isEditing={order?._id}
          title={label}
        />
      )}
    </div>
  );
};

export default OrderDetailField;
